/**
 * REST client for academy billing (F3-H). Financial data is SERVER-ONLY (no
 * PowerSync in v1), so it goes through `@/lib/rest`'s shared transport
 * —the only piece that knows how to authenticate on web (cookie-BFF) and
 * on Tauri (Bearer injected in Rust)— instead of through the local-first
 * store. Wrapped in TanStack Query for cache/loading states.
 */
import { createSafeQuery } from "@/lib/api";
import { createMutation, useQueryClient } from "@tanstack/solid-query";
import { queryKeys } from "@space/shared";
import { apiJson } from "@/lib/rest";

const BASE = "/api/billing";
// Billing administration: under /api/billing/admin (auth-only; the handlers
// compose ManageBilling ∥ org admin), NOT under the platform's /api/admin.
const ADMIN_BASE = `${BASE}/admin`;

const orgSuffix = (org?: string): string =>
  org ? `?${new URLSearchParams({ org }).toString()}` : "";

// ─── Types (mirror of academy_billing.rs handler's DTOs) ─────────────────────

export interface DebtSummary {
  totalDebtUsdMinor: number;
  openInvoiceCount: number;
  overdueInvoiceCount: number;
  nextDueDate?: string;
  vesRate?: number;
  totalDebtVesMinor?: number;
  organizationId: string;
}

export interface Invoice {
  id: string;
  concept: string;
  seqLabel?: string | null;
  amountUsdMinor: number;
  remainingUsdMinor: number;
  /** Remainder in VES, already converted by the server. The frontend does NOT reconvert. */
  remainingVesMinor?: number;
  status: "open" | "partial" | "paid" | "void";
  dueDate?: string | null;
  overdue: boolean;
  studentUserId: string;
  // F3-I: student's name/phone for "Collect via WhatsApp" (overdue rows).
  studentName?: string | null;
  studentPhone?: string | null;
}

export interface Payment {
  id: string;
  studentUserId: string;
  methodKey: string;
  currency: "USD" | "VES";
  amountMinor: number;
  amountUsdMinor: number;
  /**
   * VES amount to render, from the server. For a payment made IN VES this is
   * what the academy received that day, not a reconversion at today's rate.
   */
  amountVesMinor?: number;
  status: "pending_verification" | "verified" | "rejected";
  referenceNumber?: string | null;
  receiptAssetKey?: string | null;
  createdAt: string;
}

/** B3 · Three-state kill-switch for a gateway, per organization. */
export type GatewayState = "active" | "draining" | "off";

export interface PaymentMethod {
  key: string;
  catalogKey: string;
  name: string;
  kind: "manual" | "online";
  currencies: ("USD" | "VES")[];
  requiresReference: boolean;
  enabled: boolean;
  /**
   * B3 · Who turned it off. Without this the UI can only say "not
   * available", and the student doesn't know whether to wait (the platform
   * will fix it) or switch methods (their academy doesn't offer it).
   */
  disabledBy?: "platform" | "organization";
  state: GatewayState;
  /** `draining` keeps settling webhooks but no longer opens new charges. */
  acceptsNewCheckout: boolean;
}

/** B3 · Row of the admin's reconciliation and draining panel. */
export interface GatewayPanelRow {
  key: string;
  name: string;
  kind: "manual" | "online";
  state: GatewayState;
  offeredByOrg: boolean;
  /** Payments IN FLIGHT: what still has to land before it can be turned off. */
  inFlightCount: number;
  oldestInFlightAt?: string | null;
  collectedUsdMinor30d: number;
  providerFeeUsdMinor30d: number;
  paymentCount30d: number;
  pendingEvents: { status: string; count: number }[];
  supports: {
    refund: boolean;
    partialRefund: boolean;
    cancel: boolean;
    reconcile: boolean;
    sandbox: boolean;
    feeReported: boolean;
  };
  confirmationModel: string;
}

/** B3 · Webhook queue event. */
export interface WebhookEvent {
  id: string;
  provider: string;
  eventId: string;
  providerRef?: string | null;
  status: "received" | "processed" | "failed" | "manual" | "ignored";
  eventStatus?: string | null;
  attempts: number;
  lastError?: string | null;
  receivedAt: string;
  paymentId?: string | null;
}

/** B4 · A prepayment's quote (everything in USD minor units; VES is display-only). */
export interface PrepayQuote {
  installmentCount: number;
  baseUsdMinor: number;
  discountUsdMinor: number;
  totalUsdMinor: number;
  /** Basis points: 1050 = 10.50%. */
  percentBp: number;
}

/** B4 · G3 — an enrollment's "Prepay and save" offer. */
export interface PrepayOffer {
  enrollmentId: string;
  eligibility:
    | "eligible"
    | "first_installment_unpaid"
    | "not_enough_installments"
    | "already_prepaid";
  quote?: PrepayQuote;
  /** The card's three amounts in VES, converted by the server. */
  baseVesMinor?: number;
  savingsVesMinor?: number;
  totalVesMinor?: number;
  vesRate?: number;
  installmentIds: string[];
}

/** B4 · G5 — owner's cash dashboard. */
export interface CashForecast {
  from: string;
  to: string;
  prepaidCollectedUsdMinor: number;
  prepayDiscountUsdMinor: number;
  projectedUsdMinor: number;
  overdueUsdMinor: number;
  expectedUsdMinor: number;
  collectedSharePercent: number;
  vesRate?: number | null;
  /** Every figure in the dashboard in VES, converted by the server (a single rounding). */
  prepaidCollectedVesMinor?: number | null;
  prepayDiscountVesMinor?: number | null;
  projectedVesMinor?: number | null;
  overdueVesMinor?: number | null;
  expectedVesMinor?: number | null;
}

export interface Rate {
  id: string;
  vesPerUsd: number;
  rateDate: string;
  source: "manual" | "bcv_auto";
}

export interface Plan {
  id: string;
  scopeType: string;
  scopeId: string;
  name: string;
  kind: string;
  amountUsdMinor: number;
  installmentCount?: number | null;
  installmentAmountUsdMinor?: number | null;
  enrollmentFeeUsdMinor: number;
  active: boolean;
}

export interface Discount {
  id: string;
  kind: string;
  code?: string | null;
  percent?: number | null;
  amountUsdMinor?: number | null;
  maxUses?: number | null;
  usedCount: number;
  active: boolean;
  // AUTONOMY: validity window manageable from the UI.
  validFrom?: string | null;
  validTo?: string | null;
}

export interface AllocationInput {
  invoiceId: string;
  amountUsdMinor: number;
}

export interface RecordPaymentInput {
  org?: string;
  studentUserId: string;
  methodKey: string;
  currency: "USD" | "VES";
  amountMinor: number;
  exchangeRateId?: string;
  referenceNumber?: string;
  receiptAssetKey?: string;
  notes?: string;
  allocations: AllocationInput[];
  /**
   * Client-generated operation id (uuid per form attempt): the server
   * honors it with uniqueness per organization — a double-submit or a
   * retry returns the payment ALREADY recorded instead of duplicating it.
   */
  clientOperationId?: string;
}

// ─── Student ──────────────────────────────────────────────────────────────────

export const useMeSummary = () =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.meSummary(),
    queryFn: () => apiJson<DebtSummary>(`${BASE}/me/summary`),
  }));

export const useMeInvoices = () =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.meInvoices(),
    queryFn: () => apiJson<Invoice[]>(`${BASE}/me/invoices`),
  }));

export const useMePayments = () =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.mePayments(),
    queryFn: () => apiJson<Payment[]>(`${BASE}/me/payments`),
  }));

export const useMethods = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.methods(org()),
    queryFn: () => apiJson<PaymentMethod[]>(`${BASE}/methods${orgSuffix(org())}`),
  }));

export const useCheckout = () =>
  createMutation(() => ({
    /**
     * B3 · Missing `methodKey` = `stripe`, which is what's already deployed.
     * The kit exists precisely so the gateway stops being a constant.
     */
    mutationFn: (input: { invoiceIds: string[]; methodKey?: string }) =>
      apiJson<{ checkoutUrl: string; intentId: string }>(`${BASE}/checkout`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  }));

// ─── B4 · Discounted prepayment (G3) ────────────────────────────────────────

export const useMePrepayOffers = () =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.mePrepayOffers(),
    queryFn: () => apiJson<PrepayOffer[]>(`${BASE}/me/prepay-offers`),
  }));

/**
 * Accepts the offer. The TWO amounts the student had in front of them are
 * sent: if the state changed while they were deciding (someone credited an
 * installment), the server rejects instead of charging them an amount they
 * didn't approve.
 */
export const useAcceptPrepay = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      enrollmentId: string;
      expectedTotalUsdMinor: number;
      expectedDiscountUsdMinor: number;
    }) =>
      apiJson<{ settlementId: string; invoiceId: string; quote: PrepayQuote }>(
        `${BASE}/me/prepay`,
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      // Accepting replaces N installments with one: the debt, the invoices
      // and the offer itself all change at once.
      void qc.invalidateQueries({ queryKey: queryKeys.billing.mePrepayOffers() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.meInvoices() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.meSummary() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() });
    },
  }));
};

// ─── Cash / staff ───────────────────────────────────────────────────────────────

export const usePayments = (org: () => string | undefined, status: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.payments(org(), status()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      const s = status();
      if (o) {
        params.set("org", o);
      }
      if (s) {
        params.set("status", s);
      }
      const qs = params.toString();
      return apiJson<Payment[]>(`${BASE}/payments${qs ? `?${qs}` : ""}`);
    },
  }));

export const useInvoices = (
  org: () => string | undefined,
  student: () => string | undefined,
  status: () => string | undefined,
  overdue: () => boolean,
) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.invoices(org(), student(), status(), overdue()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      const st = student();
      const s = status();
      if (o) {
        params.set("org", o);
      }
      if (st) {
        params.set("studentUserId", st);
      }
      if (s) {
        params.set("status", s);
      }
      if (overdue()) {
        params.set("overdue", "true");
      }
      const qs = params.toString();
      return apiJson<Invoice[]>(`${BASE}/invoices${qs ? `?${qs}` : ""}`);
    },
  }));

export const useRates = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.rates(org()),
    queryFn: () => apiJson<Rate[]>(`${BASE}/rates${orgSuffix(org())}`),
  }));

export const usePlans = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.plans(org()),
    queryFn: () => apiJson<Plan[]>(`${ADMIN_BASE}/plans${orgSuffix(org())}`),
  }));

export const useDiscounts = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.discounts(org()),
    queryFn: () => apiJson<Discount[]>(`${ADMIN_BASE}/discounts${orgSuffix(org())}`),
  }));

// ── AUTONOMY: self-service billing configuration (admin.billing) ────────────

/** Org's billing config: `{"methods.enabled": string[], "receipt.footer": string}`. */
export const useBillingConfig = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.config(org()),
    queryFn: () => apiJson<Record<string, unknown>>(`${ADMIN_BASE}/config${orgSuffix(org())}`),
  }));

export const usePutBillingConfig = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; key: string; value: unknown }) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/config`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.configAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.methodsAll() });
    },
  }));
};

export const useCreatePlan = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      scopeType: string;
      scopeId: string;
      name: string;
      kind: string;
      amountUsdMinor: number;
      installmentCount?: number;
      installmentAmountUsdMinor?: number;
      enrollmentFeeUsdMinor?: number;
    }) =>
      apiJson<{ id: string }>(`${ADMIN_BASE}/plans`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.plansAll() }),
  }));
};

export const useUpdatePlan = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { id: string; active?: boolean; name?: string }) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/plans/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: input.active, name: input.name }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.plansAll() }),
  }));
};

export const useCreateDiscount = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      kind: string;
      code?: string;
      percent?: number;
      amountUsdMinor?: number;
      maxUses?: number;
      validFrom?: string;
      validTo?: string;
    }) =>
      apiJson<{ id: string }>(`${ADMIN_BASE}/discounts`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.discountsAll() }),
  }));
};

export const useUpdateDiscount = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      id: string;
      active?: boolean;
      maxUses?: number;
      validFrom?: string;
      validTo?: string;
      clearValidity?: boolean;
    }) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/discounts/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          active: input.active,
          maxUses: input.maxUses,
          validFrom: input.validFrom,
          validTo: input.validTo,
          clearValidity: input.clearValidity,
        }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.discountsAll() }),
  }));
};

// ── end AUTONOMY ─────────────────────────────────────────────────────────────

export const useRecordPayment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: RecordPaymentInput) =>
      apiJson<{ id: string }>(`${BASE}/payments`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.paymentsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() });
    },
  }));
};

export const useAddRate = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; vesPerUsd?: number; rateDate?: string; auto?: boolean }) =>
      apiJson<{ id: string }>(`${BASE}/rates`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.ratesAll() }),
  }));
};

export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      org?: string;
      studentUserId: string;
      concept: string;
      amountUsdMinor: number;
      dueDate?: string;
      enrollmentId?: string;
    }) =>
      apiJson<{ id: string }>(`${BASE}/invoices`, { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() }),
  }));
};

export const useGenerateInvoices = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: {
      enrollmentId: string;
      org?: string;
      studentUserId: string;
      planId: string;
      startDate?: string;
      discountCode?: string;
    }) =>
      apiJson<{ invoiceIds: string[] }>(
        `${BASE}/enrollments/${encodeURIComponent(input.enrollmentId)}/generate-invoices`,
        { method: "POST", body: JSON.stringify(input) },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() }),
  }));
};

// ─── Admin: verification ────────────────────────────────────────────────────────

export const useVerifyPayment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (paymentId: string) =>
      apiJson<{ ok: boolean }>(`${ADMIN_BASE}/payments/${encodeURIComponent(paymentId)}/verify`, {
        method: "POST",
        body: "{}",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.paymentsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.invoicesAll() });
    },
  }));
};

export const useRejectPayment = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { paymentId: string; reason: string }) =>
      apiJson<{ ok: boolean }>(
        `${ADMIN_BASE}/payments/${encodeURIComponent(input.paymentId)}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ reason: input.reason }),
        },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.billing.paymentsAll() }),
  }));
};

// ─── B3 · Gateway operations (admin) ────────────────────────────────────────

/** Reconciliation and draining panel: state, in-flight money and queue per gateway. */
export const useGatewayPanel = (org: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.gateways(org()),
    queryFn: () => apiJson<GatewayPanelRow[]>(`${ADMIN_BASE}/gateways${orgSuffix(org())}`),
  }));

/**
 * Three-state kill-switch. **`draining` is the operational value**: it cuts
 * off new charges and lets what's in flight land. `off` only after full
 * draining or in the face of a serious incident — and not even then does it
 * discard a webhook: it persists it to settle by hand.
 */
export const useSetGatewayState = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; key: string; state: GatewayState }) =>
      apiJson<{ ok: boolean; inFlightCount: number }>(
        `${ADMIN_BASE}/gateways/${encodeURIComponent(input.key)}/state${orgSuffix(input.org)}`,
        { method: "POST", body: JSON.stringify({ state: input.state }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.gatewaysAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.methodsAll() });
    },
  }));
};

export const useWebhookEvents = (org: () => string | undefined, status: () => string | undefined) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.webhookEvents(org(), status()),
    queryFn: () => {
      const params = new URLSearchParams();
      const o = org();
      const s = status();
      if (o) {
        params.set("org", o);
      }
      if (s) {
        params.set("status", s);
      }
      const qs = params.toString();
      return apiJson<WebhookEvent[]>(`${ADMIN_BASE}/webhook-events${qs ? `?${qs}` : ""}`);
    },
  }));

/** MANUAL settlement of an event the queue couldn't apply on its own (audited). */
export const useSettleWebhookEvent = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { org?: string; id: string; note: string }) =>
      apiJson<{ ok: boolean }>(
        `${ADMIN_BASE}/webhook-events/${encodeURIComponent(input.id)}/settle${orgSuffix(input.org)}`,
        { method: "POST", body: JSON.stringify({ note: input.note }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billing.webhookEventsAll() });
      void qc.invalidateQueries({ queryKey: queryKeys.billing.gatewaysAll() });
    },
  }));
};

/** B4 · G5 — owner's cash dashboard (collected upfront vs. projected). */
export const useCashForecast = (
  org: () => string | undefined,
  from: () => string,
  to: () => string,
) =>
  createSafeQuery(() => ({
    queryKey: queryKeys.billing.cashForecast(org(), from(), to()),
    queryFn: () => {
      const params = new URLSearchParams({ from: from(), to: to() });
      const o = org();
      if (o) {
        params.set("org", o);
      }
      return apiJson<CashForecast>(`${ADMIN_BASE}/cash-forecast?${params.toString()}`);
    },
  }));

/** URL of a payment's printable receipt (self-contained HTML). */
export const receiptUrl = (paymentId: string): string =>
  `${BASE}/receipt/${encodeURIComponent(paymentId)}`;
